import {FC, useState, Suspense, useEffect} from "react";
import Modal from "react-responsive-modal";
import {Formik} from "formik";
import {fetchQuery, useRelayEnvironment} from "react-relay";
import toast, {Toaster} from "react-hot-toast";
import {uploadWorkOrderPdf} from "../../models/_location";
import {graphql} from "babel-plugin-relay/macro";
import {useQuery} from "react-query";
import {generateMultiSignedUrls} from "../../models/_publicCall";
import {FOLDER_NAME} from "../../../_metronic/helpers/enums";

type Props = {
  currentLocationId: string;
  handleUploadPdfClose: () => void;
  uploadPdfModal: boolean;
  refreshTable?: any;
};

export const getLocationByIdForUploadPdf = graphql`
  query UploadWorkOrderPdfQuery($id: String!) {
    getLocation(id: $id) {
      id
      uuid
      name
      workOrderPdf
    }
  }
`;

export const UploadPdf: FC<Props> = ({
  currentLocationId,
  uploadPdfModal,
  handleUploadPdfClose,
  refreshTable,
}) => {
  const environment = useRelayEnvironment();

  const {data: locationRecord} = useQuery(
    ["getLocationByIdForUploadPdf", currentLocationId],
    () =>
      fetchQuery(environment, getLocationByIdForUploadPdf, {
        id: currentLocationId ?? undefined,
      }).toPromise(),
    {
      enabled:uploadPdfModal && !!currentLocationId,
    }
  );

  // defensively extract location
  const locations: any =
    locationRecord &&
    typeof locationRecord === "object" &&
    "getLocation" in locationRecord
      ? locationRecord.getLocation
      : null;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const fileName = locations?.workOrderPdf
    ? decodeURIComponent(
        new URL(locations?.workOrderPdf).pathname.split("/").pop() || ""
      )
    : "";
  const requestSignedUrlForPdf = async (name: string, type: string) => {
    const items = await generateMultiSignedUrls([
      {
        file_name: name,
        file_type: type,
        folder_name: FOLDER_NAME.WORK_ORDER_PDF,
      },
    ]);
    return items?.[0]?.url || "";
  };

  const uploadToSignedUrl = async (
    signedUrl: string,
    fileOrBlob: Blob,
    type: string
  ) => {
    const res = await fetch(signedUrl, {
      method: "PUT",
      headers: {"Content-Type": type},
      body: fileOrBlob,
    });
    if (!res.ok) throw new Error("Upload failed");
    return signedUrl.split("?")[0];
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => {
    const input = event.currentTarget;
    const fileList = input.files;
    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];
    // Validate PDF
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      toast.error("Please select a PDF file.");
      input.value = "";
      return;
    }

    setUploading(true);
    setSelectedFileName(file.name);

    try {
      const signedUrl = await requestSignedUrlForPdf(
        file.name,
        file.type || "application/pdf"
      );
      if (!signedUrl) {
        toast.error("Failed to get upload URL. Please try again.");
        return;
      }

      const publicUrl = await uploadToSignedUrl(
        signedUrl,
        file,
        file.type || "application/pdf"
      );

      // set the formik field so onSubmit will have fileUrl
      setFieldValue("fileUrl", publicUrl);
    } catch (error) {
      console.error("PDF upload error", error);
      toast.error("Failed to upload PDF. Please try again.");
    } finally {
      setUploading(false);
      // clear input value so user can re-upload same file if needed
      input.value = "";
    }
  };

  useEffect(() => {
    if (uploadPdfModal) {
      setSelectedFileName(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadPdfModal]);

  return (
    <Suspense>
      <Modal
        open={uploadPdfModal && !!currentLocationId}
        onClose={() => handleUploadPdfClose()}
        role="modal"
        center
        classNames={{
          root: "content-wrapper",
        }}
      >
        <Formik
          enableReinitialize
          initialValues={{
            fileUrl: "",
          }}
          onSubmit={async (values, {resetForm}) => {
            try {
              setLoading(true);
              if (!locations?.id) {
                toast.error("Location not found.");
                return;
              }

              if (!values.fileUrl) {
                toast.error("Please upload a PDF first.");
                return;
              }

              const updateData: any = {
                locationId: locations.id,
                fileUrl: values.fileUrl,
              };
              const result = await uploadWorkOrderPdf(updateData);

              if (result?.data?.data?.uploadWorkOrderPdf) {
                toast.success("Work Order Pdf Uploaded successfully");
                resetForm();
                setSelectedFileName(null);
                if (typeof refreshTable !== "undefined") {
                  refreshTable();
                }
                handleUploadPdfClose();
              } else {
                const messages = result?.data?.errors?.map(
                  (e: any) => e.message
                ) || ["Unknown error"];
                toast.error(messages.join(","));
              }
            } catch (error) {
              console.log("Error in updating sales information", error);
              toast.error("Failed to update.");
            } finally {
              setLoading(false);
            }
          }}
        >
          {({handleSubmit, values, setFieldValue}) => {
            return (
              <form
                id="kt_modal_update_location_price_form"
                className="form"
                onSubmit={handleSubmit}
                autoComplete="off"
                noValidate
              >
                <div className="modal-header p-2 pb-4 mb-4">
                  <h4 className="modal-title fs-1 mt-4">
                    Work Order Pdf - {locations?.name ?? ""}
                  </h4>
                </div>
                <div className="modal-body">
                <div className="row mt-2 pt-2">
                    {/* Existing Work Order PDF */}
                    <div className="col-12 mb-3">
                      <div className="row g-2 align-items-center">
                        <div className="col-md-4 col-12">
                          <label className="form-label fs-5 mb-0">
                            Existing Work Order Pdf:
                          </label>
                        </div>
                        <div className="col-md-8 col-12">
                          <a
                            href={locations?.workOrderPdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                            style={{cursor: "pointer"}}
                          >
                            {fileName ?? "-"}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Upload Work Order PDF */}
                    <div className="col-12 mb-4">
                      <div className="row g-2 align-items-center">
                        <div className="col-md-4 col-12">
                          <label className="form-label fs-5 mb-0">
                            Upload Work Order Pdf:
                          </label>
                        </div>
                        <div className="col-md-8 col-12">
                          <input
                            type="file"
                            accept="application/pdf"
                            className="form-control"
                            onChange={(e) => handleFileChange(e, setFieldValue)}
                            disabled={uploading}
                          />

                          {values.fileUrl && selectedFileName && (
                            <p className="mt-2 text-primary">
                              Selected: {selectedFileName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer mt-6 p-0 justify-content-start">
                    <button
                      type="submit"
                      className="btn btn-primary me-4 fs-4 p-0 px-6 py-3 mt-6"
                      disabled={loading || uploading}
                    >
                      {loading || uploading ? (
                        <span
                          className="indicator-progress"
                          style={{display: "block"}}
                        >
                          {uploading ? "Uploading PDF..." : "Please Wait..."}
                          <span className="spinner-border spinner-border-sm align-middle ms-3 mb-1"></span>
                        </span>
                      ) : (
                        <span className="indicator-label">Upload</span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </Modal>

      <Toaster
        position="bottom-left"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: "",
          duration: 3000,
        }}
      />
    </Suspense>
  );
};
